/**
 * "Files waiting to be sent" (CT-15747; cs-analysis docs/design-client-comms.md §4.1 "See and drop"; the Infurnia Chat
 * app's screen, cs-analysis apps/rep-chat src/HeldFiles.tsx over packages/comms-client held-files.ts, whose words these
 * are): the files this representative uploaded or copied for a post that no message has taken yet — exactly what the
 * upload's 409 MEDIA_UNATTACHED_LIMIT counts — to see, and to remove one. The representatives' routes only, through
 * cs_analysis_fetch (the CSP's cookie and the staff CSRF value): `GET /api/v1/comms/media/held` and
 * `DELETE /api/v1/comms/media/held/:mediaId`. The client tier's `/api/v1/client/comms/media/held` is never called here.
 *
 * The rules (specs: held_files.test.js); HeldFiles.jsx draws them:
 *  1. The server decides. The list, its count and its limit are the list's answer; the CSP never counts files or
 *     mirrors the cap. A Remove's outcome is the drop's answer: dropped → the row goes and the count is the answer's;
 *     404 MEDIA_NOT_HELD → the row goes quietly (dropped already, cleared on its own: nothing to say); 409
 *     MEDIA_ALREADY_ATTACHED → the row goes with a quiet "Already sent"; anything else → "Could not remove. Try
 *     again." on the row, which stays. A code counts only with its own status. The error goes to the log by its tag
 *     (error_tag) only — never the api's words, a code or a status on screen.
 *  2. The upload limit's line gains "See files" (offer_held_files): at the refusal the composer reads the list once. An
 *     api from before the routes (cs-analysis #494) answers 404 / 405: the composer keeps today's line exactly (the
 *     alert), and no "See files" anywhere — it hides itself until the api has the routes. Any other answer puts the
 *     line under the composer with "See files" (a list that does not load says so, with Try again).
 *  3. A file one of the CSP's own composers still holds — the attachment chip of a message or a ticket not sent yet,
 *     or an annotation between its upload and its post — is marked "In this message" and offers NO Remove. The
 *     composer posts the chip's media id from its own state when Send is pressed, and a post in flight already carries
 *     it, so a drop of that file could always race a post naming it; with no Remove the CSP never sends a message
 *     pointing at a file it removed. The chip's own ✕ takes the file out of the message; it is then an ordinary row
 *     with Remove.
 *  4. The count against the limit at the top ("12 of 30"); each row its name, its type and size, when it was added and
 *     when it is cleared on its own ("Cleared on its own in 3 h", from `expiresAt`). Empty: "Nothing waiting.".
 */
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';

/** The representatives' route (the staff tier). Never the client tier's `/api/v1/client/comms/media/held`. */
export const HELD_MEDIA_PATH = '/api/v1/comms/media/held';

export const MEDIA_NOT_HELD = 'MEDIA_NOT_HELD';
export const MEDIA_ALREADY_ATTACHED = 'MEDIA_ALREADY_ATTACHED';

export const HELD_WORDS = {
    title: 'Files waiting to be sent',
    see_files: 'See files',
    see_files_hint: 'Shows your files waiting to be sent, to remove some',
    count_of: 'files waiting to be sent',
    about: (limit) =>
        `Files you added to messages that are not sent yet. While ${limit} are waiting, no more can be added until some are sent, removed or cleared on their own.`,
    empty: 'Nothing waiting.',
    loading: 'Loading the files',
    load_failed: 'Could not load the files. Try again.',
    not_on_server: 'This Infurnia server cannot show them yet.',
    try_again: 'Try again',
    remove: 'Remove',
    remove_label: (name) => `Remove ${name}`,
    removing: 'Removing…',
    remove_failed: 'Could not remove. Try again.',
    already_sent: 'Already sent',
    in_message: 'In this message',
    in_message_hint: 'To remove it, take it out of the message first (×).', // × as the chip's close icon draws (T004)
    close: 'Close',
    dismiss: 'Dismiss',
    no_name: 'File',
};

/** One log line; never shown. */
export const held_log = (line) => console.info(`[held_files] ${line}`);

const code_of = (e) => (e && e.body && typeof e.body === 'object' && typeof e.body.code === 'string' ? e.body.code : null);

/** What a log line says of an error: its status and code, else its name. Never its message (the api's words). */
export const error_tag = (e) => {
    if (e && typeof e.status === 'number') return [e.status, code_of(e)].filter((x) => x !== null).join(' ');
    if (e instanceof Error) return e.name || 'Error';
    return typeof e;
};

/** The list route answered as an api from before it: 404 / 405 (rule 2). Only the list's GET is read this way. */
const no_held_route = (e) => !!e && (e.status === 404 || e.status === 405);
export const is_no_held_route = (e) => no_held_route(e);

/** What a Remove's refusal means for its row (rule 1): 'gone' | 'already-sent' | 'failed'. */
export const held_drop_refusal = (e) => {
    if (!e || typeof e.status !== 'number') return 'failed';
    const code = code_of(e);
    if (e.status === 404 && code === MEDIA_NOT_HELD) return 'gone';
    if (e.status === 409 && code === MEDIA_ALREADY_ATTACHED) return 'already-sent';
    return 'failed';
};

const is_count = (n) => typeof n === 'number' && Number.isInteger(n) && n >= 0;

/** The list's answer when it is the contract's shape, else null (the list says it could not load). */
export const parse_held_list = (raw) => {
    if (!raw || typeof raw !== 'object') return null;
    if (!is_count(raw.limit) || !is_count(raw.count) || !Array.isArray(raw.files)) return null;
    const files = [];
    for (const f of raw.files) {
        if (!f || typeof f !== 'object' || typeof f.mediaId !== 'string' || f.mediaId === '') return null;
        files.push({
            mediaId: f.mediaId,
            filename: typeof f.filename === 'string' ? f.filename : '',
            mimetype: typeof f.mimetype === 'string' ? f.mimetype : '',
            size: is_count(f.size) ? f.size : 0,
            createdAt: typeof f.createdAt === 'string' ? f.createdAt : '',
            expiresAt: typeof f.expiresAt === 'string' ? f.expiresAt : '',
        });
    }
    return { limit: raw.limit, count: raw.count, files };
};

/** The list: resolves with the server's raw answer (parse_held_list reads it); a non-2xx throws with its status. */
export const fetch_held = () => cs_analysis_fetch({ path: HELD_MEDIA_PATH });

/** Drop one: resolves `{ mediaId, limit, count }`; refusals 404 MEDIA_NOT_HELD, 409 MEDIA_ALREADY_ATTACHED. */
export const drop_held = (media_id) => cs_analysis_fetch({ path: `${HELD_MEDIA_PATH}/${encodeURIComponent(media_id)}`, method: 'DELETE' });

/**
 * The upload was refused 409 MEDIA_UNATTACHED_LIMIT: read the list once (rule 2). True → the composer shows the line
 * with "See files"; false → the api has no such route (404 / 405) and the caller says today's line as before.
 */
export const offer_held_files = async () => {
    try {
        await fetch_held();
    } catch (e) {
        if (no_held_route(e)) {
            held_log(`no list route on this api (${error_tag(e)}): today's line`);
            return false;
        }
        held_log(`the files waiting did not load at the limit (${error_tag(e)})`);
    }
    return true;
};

/** "12 of 30": what the representative holds against the most one person may hold (the server's numbers). */
export const held_count_line = (count, limit) => `${count} of ${limit}`;

/** At or past the limit: the count is drawn as a warning. */
export const held_at_limit = (count, limit) => count >= limit;

const MINUTE_MS = 60000;
const HOUR_MS = 60 * MINUTE_MS;

/** "Cleared on its own in 3 h" (hours rounded up), under an hour "in 40 min", then "now"; '' when unreadable. */
export const held_clears_line = (expires_at, now_ms) => {
    const at = Date.parse(expires_at);
    if (Number.isNaN(at)) return '';
    const left = at - now_ms;
    if (left <= 0) return 'Cleared on its own now';
    if (left < HOUR_MS) return `Cleared on its own in ${Math.ceil(left / MINUTE_MS)} min`;
    return `Cleared on its own in ${Math.ceil(left / HOUR_MS)} h`;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const day_key = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/** "Added 10:42" today, "Added yesterday, 18:30", else "Added 12 Sept, 09:05". '' when the time cannot be read. */
export const held_added_line = (created_at, now_ms) => {
    const d = new Date(created_at);
    if (Number.isNaN(d.getTime())) return '';
    const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    const now = new Date(now_ms);
    if (day_key(d) === day_key(now)) return `Added ${time}`;
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    if (day_key(d) === day_key(yesterday)) return `Added yesterday, ${time}`;
    const day = `${d.getDate()} ${MONTHS[d.getMonth()]}${d.getFullYear() === now.getFullYear() ? '' : ` ${d.getFullYear()}`}`;
    return `Added ${day}, ${time}`;
};

/** The file's type in short: its name's extension ("PDF", "XLSX"), else its family from the type, else "File". */
export const held_type_label = (mimetype, filename) => {
    const ext = /\.([a-z0-9]{1,5})$/i.exec(String(filename || '').trim());
    if (ext) return ext[1].toUpperCase();
    const family = String(mimetype || '').split('/')[0];
    if (family === 'image') return 'Image';
    if (family === 'video') return 'Video';
    if (family === 'audio') return 'Audio';
    return 'File';
};

/** "2.3 MB", "801 KB", "900 B". */
export const held_size = (n) => {
    if (!is_count(n)) return '';
    if (n > 1048576) return `${(n / 1048576).toFixed(1)} MB`;
    if (n > 1024) return `${Math.round(n / 1024)} KB`;
    return `${n} B`;
};

/**
 * The list after a refusal that says the file is not held any more ('gone', 'already-sent'): without the row, and the
 * count one less — the server's own count less the one file it has just said it does not count.
 */
export const held_without = (list, media_id) => {
    const files = list.files.filter((f) => f.mediaId !== media_id);
    return files.length === list.files.length ? list : { limit: list.limit, count: Math.max(0, list.count - 1), files };
};

// ── The chips the CSP's composers hold (rule 3): page-wide, as the list is the person's. ──

const chips = new Map();
const chip_listeners = new Set();
const bump = () => chip_listeners.forEach((l) => l());

/** A composer holds `media_id` until the returned release is called (sent, ✕, unmounted). */
export const hold_chip = (media_id) => {
    const key = {};
    chips.set(key, media_id);
    bump();
    return () => {
        if (chips.delete(key)) bump();
    };
};

export const subscribe_chips = (listener) => {
    chip_listeners.add(listener);
    return () => chip_listeners.delete(listener);
};

/** The media ids the composers hold now. */
export const chip_media_ids = () => new Set(chips.values());
